import React from "react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { RoomProvider, useRoom } from "./RoomProvider";
import { useAuth } from "../../lib/auth";
import { useFloorMode } from "../../hooks/useFloorMode";
import { usePrivacyMode } from "../../hooks/usePrivacyMode";
import { useSystemMode } from "../../hooks/useSystemMode";
import RoomCanvas from "./RoomCanvas";
import Coworker from "./Coworker";
import CommandLine from "./CommandLine";
import FocusWorkspace from "./FocusWorkspace";
import PeripheralAwareness from "./PeripheralAwareness";
import CinematicOverlay from "./CinematicOverlay";
import Onboarding from "./Onboarding";
import TripTray from "./TripTray";
import IncomingOffersLane from "./IncomingOffersLane";
import KaizenIntakeLog from "./KaizenIntakeLog";
import ApprovalsLane from "./ApprovalsLane";
import CPDLane from "./CPDLane";
import SystemBannerStack from "./SystemBannerStack";
import CallBreakoutRoom from "./CallBreakoutRoom";
import PulsePanel from "./PulsePanel";
import ReclaimBar from "./ReclaimBar";
import ProvidersPage from "./ProvidersPage";
import ShiftHandoff from "./ShiftHandoff";
import MonitoringConsentGate from "./MonitoringConsentGate";
import FloorDialer from "./FloorDialer";
import Softphone from "../../components/softphone/Softphone";
import { useFloorActivityMonitor } from "../../hooks/useFloorActivityMonitor";
import { useAttentionQueue } from "../../hooks/useAttentionQueue";
import { playChime } from "../../lib/chime";
import "./floor.css";

const VA_ROLES = new Set(["admin", "va", "dispatcher"]);

export default function FloorPage() {
  return (
    <RoomProvider>
      <FloorInner />
    </RoomProvider>
  );
}

function FloorInner() {
  const { sosTripId } = useRoom();
  const { user } = useAuth();
  const mode = useFloorMode();
  const privacy = usePrivacyMode();
  const location = useLocation();
  const providersPageOpen = location.pathname === "/floor/providers";
  const isVA = user?.role ? VA_ROLES.has(user.role) : true;
  const [monitoringReady, setMonitoringReady] = React.useState(false);
  useFloorActivityMonitor(isVA && monitoringReady);
  const q = useAttentionQueue();
  // Any open risk/chain alert takes over the room's identity strip, not just
  // "critical" ones — a "high" severity stale-test-trip alert used to sit
  // quietly in the PulsePanel badge where nobody noticed it needed action.
  const topCriticalRisk = !sosTripId ? q.risks.find(r => r.severity === "critical" || r.severity === "high") : null;

  // Room takeover reaches the VA by ear too, not just by sight — fires once
  // per new SOS/critical-risk event, never repeats for the same one.
  const lastChimeRef = React.useRef(null);
  React.useEffect(() => {
    const key = sosTripId ? `sos:${sosTripId}` : topCriticalRisk ? `risk:${topCriticalRisk.alert_id}` : null;
    if (key && key !== lastChimeRef.current) {
      playChime(sosTripId ? "sos" : "risk");
    }
    lastChimeRef.current = key;
  }, [sosTripId, topCriticalRisk]);

  return (
    <div className={`floor floor--${mode} ${sosTripId ? "floor--sos" : topCriticalRisk ? "floor--risk" : ""} ${privacy.on ? "floor--privacy" : ""}`}
         data-testid="floor-root">
      {isVA && <MonitoringConsentGate onReady={() => setMonitoringReady(true)} />}
      <FloorMark privacy={privacy} isVA={isVA} topCriticalRisk={topCriticalRisk} />
      <ReclaimBar />
      <RoomCanvas />
      {isVA && <PulsePanel />}
      {/* One coordinated column instead of 3 independently absolute-positioned
          widgets guessing each other's height — was cascading on top of each
          other whenever the offers list or Coworker's whispers grew tall. */}
      <div className="floor-left-rail" data-testid="floor-left-rail">
        {isVA && <IncomingOffersLane />}
        {isVA && <KaizenIntakeLog />}
        {isVA && <ApprovalsLane />}
        <Coworker />
      </div>
      {isVA && <CPDLane />}
      <SystemBannerStack showApprovalCalls={isVA} />
      <FocusWorkspace />
      <TripTray />
      <PeripheralAwareness />
      <CinematicOverlay />
      <CommandLine />
      <Onboarding />
      <CallBreakoutRoom />
      {isVA && <FloorDialer />}
      {isVA && <Softphone />}
      {isVA && <ShiftHandoff />}
      {isVA && providersPageOpen && <ProvidersPage />}
    </div>
  );
}

// The one persistent piece of chrome — a whisper of identity.
function FloorMark({ privacy, isVA, topCriticalRisk }) {
  const { identity, sosTripId, findTrip, setDialerOpen, focusOn, resolveChainAlert, resolveTripRiskAlert, archiveTrip } = useRoom();
  const navigate = useNavigate();
  const sysMode = useSystemMode();
  const sosTrip = sosTripId ? findTrip(sosTripId) : null;
  const [newTripOpen, setNewTripOpen] = React.useState(false);
  const [riskBusy, setRiskBusy] = React.useState(false);

  if (sosTrip) {
    return (
      <div className="floor-mark sos" data-testid="floor-mark">
        <span className="fm-glow sos" />
        <span>🚨 SOS · {String(sosTrip.trip_id).slice(0, 8)} · {(sosTrip.rider?.name || "rider").toUpperCase()} · THE ROOM IS ON THIS</span>
      </div>
    );
  }

  // Nova's most severe unresolved risk takes over the room's identity strip —
  // same "the room is on this" treatment as SOS (one tier down), so a critical
  // pickup-risk reaches the VA no matter what workspace they're focused in,
  // instead of waiting for them to notice a count change in the PulsePanel.
  if (topCriticalRisk && isVA) {
    const resolveRisk = async (action) => {
      const o = topCriticalRisk.suggested_option;
      setRiskBusy(true);
      try {
        if (topCriticalRisk.kind === "trip_risk_alert") {
          if (action === "reassign" && o) await resolveTripRiskAlert(topCriticalRisk.alert_id, "reassign", o.driver_id, o.tp_id);
          else await resolveTripRiskAlert(topCriticalRisk.alert_id, "dismiss");
        } else if (topCriticalRisk.kind === "chain_alert") {
          if (action === "reassign" && o) await resolveChainAlert(topCriticalRisk.alert_id, "reassign", o.driver_id, o.tp_id);
          else await resolveChainAlert(topCriticalRisk.alert_id, "dismiss");
        }
      } finally {
        setRiskBusy(false);
      }
    };
    const doArchive = async () => {
      if (!topCriticalRisk.trip_id) return;
      setRiskBusy(true);
      try { await archiveTrip(topCriticalRisk.trip_id); } finally { setRiskBusy(false); }
    };
    return (
      <div className="floor-mark risk" data-testid="floor-mark">
        <span className="fm-glow risk" />
        <span>⚡ NOVA · {topCriticalRisk.label} · {topCriticalRisk.sub} · THE ROOM IS ON THIS</span>
        {topCriticalRisk.suggested_option && (
          <button className="fm-risk-btn" disabled={riskBusy} onClick={() => resolveRisk("reassign")}
                  data-testid="floor-mark-risk-reassign">
            {riskBusy ? "…" : `reassign to ${topCriticalRisk.suggested_option.name}`}
          </button>
        )}
        <button className="fm-risk-btn fm-risk-btn--ghost" disabled={riskBusy} onClick={() => resolveRisk("dismiss")}
                data-testid="floor-mark-risk-dismiss">
          dismiss
        </button>
        {topCriticalRisk.trip_id && (
          <button className="fm-risk-btn fm-risk-btn--archive" disabled={riskBusy} onClick={doArchive}
                  data-testid="floor-mark-risk-archive"
                  title="Stale or test trip? Archive it and clear this alert for good.">
            archive trip
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="floor-mark" data-testid="floor-mark">
      <button className="fm-back" onClick={() => navigate("/admin")} data-testid="floor-back-to-admin"
              title="Back to Admin">← admin</button>
      <span className="fm-glow" />
      <span>MedexOVA · The Floor</span>
      {isVA && (
        <button className="fm-dialer" onClick={() => setDialerOpen(true)} data-testid="floor-open-dialer-btn"
                title="Call any on-shift driver, or dial a raw phone number">
          ☎ dialer
        </button>
      )}
      {isVA && (
        <button className="fm-providers" onClick={() => navigate("/floor/providers")} data-testid="floor-open-providers-btn"
                title="Full provider roster — opens its own page">
          ▤ providers
        </button>
      )}
      {isVA && (
        <div className="fm-new-trip-wrap">
          <button className="fm-new-trip" onClick={() => setNewTripOpen((v) => !v)} data-testid="floor-new-trip-btn"
                  title="Create a Standard or CPD trip right here on The Floor">
            + new trip
          </button>
          {newTripOpen && (
            <div className="fm-new-trip-menu" data-testid="floor-new-trip-menu">
              <button onClick={() => { focusOn("compose", null); setNewTripOpen(false); }}
                      data-testid="floor-new-trip-standard">Standard trip</button>
              <button onClick={() => { focusOn("composeCpd", null); setNewTripOpen(false); }}
                      data-testid="floor-new-trip-cpd">CPD trip</button>
            </div>
          )}
        </div>
      )}
      {identity?.dispatcher_name && (
        <span style={{ marginLeft: 12, color: "var(--ink-mute)", letterSpacing: "0.24em" }}>
          · {identity.dispatcher_name.split(" ")[0].toLowerCase()} on shift
        </span>
      )}
      {privacy && (
        <button className={`fm-privacy ${privacy.on ? "on" : ""}`}
                onClick={privacy.toggle}
                data-testid="privacy-toggle"
                title="Toggle PHI masking across whispers, tooltips, incoming lane">
          {privacy.on ? "PRIVACY · ON" : "privacy"}
        </button>
      )}
      {isVA && sysMode.loaded && <SystemModePill sysMode={sysMode} />}
    </div>
  );
}

// ─── System Mode — ONE global switch, shown prominently so whomever is
// running the Floor always knows exactly how the system will behave.
// Standard Mode's Kaizen behavior is fixed (accept-then-notify, never a
// pre-approval call); Away Mode's behavior is whatever the admin
// configured under Settings → Mode & Autonomy. Switching requires an
// explicit second click — this now affects the whole desk, not just
// whoever clicks it.
function SystemModePill({ sysMode }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  if (sysMode.degraded) {
    return (
      <span className="fm-mode-pill fm-mode-pill--degraded" data-testid="system-mode-pill-degraded"
            title="Mode could not be verified — automatic Kaizen acceptance and driver assignment are blocked until this is fixed.">
        MODE UNVERIFIED
      </span>
    );
  }
  const isAway = sysMode.mode === "away";
  const kz = sysMode.policies?.kaizen_acquisition?.away;
  const awayDesc = kz?.autonomy_level === "fully_autonomous"
    ? "fully autonomous — auto-accepts AND auto-assigns a driver"
    : "accept-then-notify — auto-accepts, VA still assigns the driver";
  const nextMode = isAway ? "standard" : "away";
  const confirmLabel = isAway
    ? "Switch to Standard — Kaizen keeps accepting instantly, VA assigns the driver, no admin-autonomy escalation."
    : `Switch to Away — Kaizen behavior becomes: ${awayDesc}.`;

  const confirm = async () => {
    setBusy(true);
    try {
      await sysMode.setMode(nextMode);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch mode — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fm-mode-wrap">
      <button
        className={`fm-mode-pill ${isAway ? "away" : "standard"}`}
        onClick={() => setOpen(o => !o)}
        data-testid="system-mode-pill"
        title="The ONE operating mode for the whole desk — click to see what it means and change it"
      >
        <span className="fm-mode-dot" />
        {isAway ? "AWAY MODE" : "STANDARD MODE"}
      </button>
      {open && (
        <div className="fm-mode-popover" data-testid="system-mode-popover">
          <div className="fm-mode-popover-current">
            Currently <strong>{isAway ? "AWAY" : "STANDARD"}</strong>: {isAway ? awayDesc : "Kaizen accepts eligible offers immediately, no pre-approval call, VA assigns the driver after."}
          </div>
          <button className="fm-mode-popover-confirm" onClick={confirm} disabled={busy} data-testid="system-mode-confirm-switch-btn">
            {busy ? "switching…" : confirmLabel}
          </button>
          <button className="fm-mode-popover-cancel" onClick={() => setOpen(false)} data-testid="system-mode-cancel-btn">cancel</button>
        </div>
      )}
    </div>
  );
}
